// ─── VENDORED — DO NOT EDIT ─────────────────────────────────────────────────
// CONTRACT:I4-POLICY-DECISION.1.0 — vendored decision/marking substrate (see README.md)
// Source: willackerly/TDFLite tag v0.10.0 (commit 2a1ec7cb639a7a77b8ec9e14df4cb0b139cb43aa)
// Path:   js/packages/classification-schema/src/lattice.ts
// Sync:   scripts/sync-classification-schema.sh (re-vendors on a pin bump)
// Drift:  scripts/check-classification-schema-drift.sh (this header + the
//         "@tdflite/contracts" specifier rewrite + the de-triggered upstream
//         contract line are NORMALIZED; any OTHER divergence FAILS the gate).
// ─── END VENDORED HEADER ─────────────────────────────────────────────────────
// upstream-contract F1-CLASSIFICATION-SCHEMA.0.1 (TDFLite; see vendored header)
/** The classification LATTICE — the ONE canonical ladder, in its complementary
 *  encodings. Single-sourced from tak-tdf abac-pdp/src/policy.ts (CLASS_ORDER :31,
 *  CLASS_LEVELS :41) so every classification comparison in the edge distribution
 *  imports the SAME ladder and the duplicate-lattice drift (the M3 mis-map) can never
 *  reappear.
 *
 *  THE ONE LADDER: TOP_SECRET > SECRET > CONFIDENTIAL > CUI > UNCLASSIFIED.
 *
 *  TWO ENCODINGS, ONE LADDER — the load-bearing subtlety to internalize:
 *
 *    (1) CLASS_ORDER — the RANK map. 0 = UNCLASSIFIED (lowest) … 4 = TOP_SECRET
 *        (highest). A HIGHER number means MORE sensitive. This is the encoding you
 *        compare when you ask "does the subject's clearance dominate the data's
 *        classification?" → read-down holds iff rank(clearance) >= rank(data).
 *
 *    (2) CLASS_LEVELS — the RANK-INDEXED ladder, low→high; the exact inverse map of
 *        CLASS_ORDER, so CLASS_LEVELS[CLASS_ORDER[x]] === x for every level x. Index 0
 *        = UNCLASSIFIED (the lowest rank). Use it to turn a rank back into a name.
 *
 *  A THIRD view is easy to confuse with the two above and MUST be kept straight: the
 *  read-down EVALUATOR (evaluate.ts) indexes the classification attribute's `values`
 *  array — CLASSIFICATION_HIERARCHY — which is ordered HIGHEST→lowest (index 0 =
 *  TOP_SECRET). There the rule is `userIdx <= minDataIdx`, so a LOWER index means a
 *  HIGHER level: its index direction is the OPPOSITE of CLASS_ORDER's rank direction.
 *  All three name the identical ladder; they merely count from opposite ends. Pinning
 *  every encoding to ONE source (here) is what keeps the directions in agreement — and
 *  latticeEquivalence() is the drift-guard that proves it. */

/** (1) The RANK map — 0 = UNCLASSIFIED (lowest) … 4 = TOP_SECRET (highest). Underscore
 *  form, CUI included. Higher number = more sensitive. Verbatim from policy.ts:31. */
export const CLASS_ORDER: Readonly<Record<string, number>> = {
  UNCLASSIFIED: 0,
  CUI: 1,
  CONFIDENTIAL: 2,
  SECRET: 3,
  TOP_SECRET: 4,
};

/** (2) The RANK-INDEXED ladder, low→high — the inverse of CLASS_ORDER, so
 *  CLASS_LEVELS[CLASS_ORDER[x]] === x. Index 0 = UNCLASSIFIED. Verbatim from
 *  policy.ts:41. */
export const CLASS_LEVELS: readonly string[] = [
  "UNCLASSIFIED",
  "CUI",
  "CONFIDENTIAL",
  "SECRET",
  "TOP_SECRET",
] as const;

/** The READ-DOWN hierarchy array the evaluator indexes: HIGHEST→lowest (index 0 =
 *  TOP_SECRET). This is CLASS_LEVELS reversed, defined ONCE here so the SEED_BUNDLE's
 *  `classification` attribute and any read-down index math share a single ladder. The
 *  evaluator's rule is userIdx <= minDataIdx, so index 0 (TOP_SECRET) is the highest. */
export const CLASSIFICATION_HIERARCHY: readonly string[] = [...CLASS_LEVELS].reverse();

const normalizeName = (name: string): string => String(name).trim().toUpperCase();

/** The pinned rank of a classification NAME (case-insensitive), or undefined when the
 *  name is not on the ladder. Undefined is the FAIL-CLOSED signal — an unknown
 *  classification dominates nothing and is dominated by nothing. */
export function classRank(name: string): number | undefined {
  return CLASS_ORDER[normalizeName(name)];
}

/** Read-down dominance: does `clearance` read down to `classification`? True iff the
 *  clearance's rank is at least the classification's rank (rank(clr) >= rank(data)).
 *  Fail-closed: an unknown clearance OR classification yields false. */
export function dominates(clearance: string, classification: string): boolean {
  const c = classRank(clearance);
  const d = classRank(classification);
  return c !== undefined && d !== undefined && c >= d;
}

/** The PINNED equivalence — the drift-guard that proves all encodings describe the SAME
 *  ladder. Returns true iff BOTH round-trips hold (CLASS_LEVELS[CLASS_ORDER[x]] === x
 *  and CLASS_ORDER[CLASS_LEVELS[i]] === i) AND the read-down hierarchy array is
 *  CLASS_LEVELS counted from the other end (rank === length-1-index). The born-with-
 *  vectors suite pins this GREEN so the two-encodings-one-ladder invariant can never
 *  silently break. */
export function latticeEquivalence(): boolean {
  const names = Object.keys(CLASS_ORDER);
  if (names.length !== CLASS_LEVELS.length) return false;
  if (CLASSIFICATION_HIERARCHY.length !== CLASS_LEVELS.length) return false;
  // name → rank → name round-trips.
  for (const x of names) {
    const r = CLASS_ORDER[x];
    if (r === undefined || CLASS_LEVELS[r] !== x) return false;
  }
  // rank → name → rank round-trips, and the read-down array is the reversed ladder.
  for (let i = 0; i < CLASS_LEVELS.length; i++) {
    const name = CLASS_LEVELS[i];
    if (name === undefined || CLASS_ORDER[name] !== i) return false;
    if (CLASSIFICATION_HIERARCHY[CLASS_LEVELS.length - 1 - i] !== name) return false;
  }
  return true;
}
